/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { useEffect } from 'react'

import { useQuery } from '@apollo/client'
import { formatDuration, intervalToDuration } from 'date-fns'
import BuildInfoQuery from 'GraphQL/Queries/buildInfo.graphql'

import { getEnv } from 'Services/Envs'

export default function useBuildInfo() {
  const webBuildDate = getEnv('BUILD_DATE', 'unknown')
  const webCommitHash = getEnv('COMMIT_HASH', 'unknown')
  const nosVersion = getEnv('GIT_TAG', 'unknown')
  const { data } = useQuery<Pick<MainSchema.Query, 'buildInfo'>>(BuildInfoQuery)
  const apiBuildDate = data?.buildInfo.apiBuildDate ?? 'unknown'
  const apiCommitHash = data?.buildInfo.apiCommitHash ?? 'unknown'

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return
    }

    let hasBeenLogged = false

    const { seconds: webSeconds, ...webDuration } = intervalToDuration({
      start: new Date(webBuildDate),
      end: new Date(),
    })
    const { seconds: apiSeconds, ...apiDuration } = intervalToDuration({
      start: new Date(apiBuildDate),
      end: new Date(),
    })

    const logBuildInfo = () => {
      if (!hasBeenLogged) {
        const css =
          'font-size: 12px; padding: 50px 0px; background: no-repeat center/100% url(data:image/svg+xml;base64,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);'
        const versionMessage = `%c          nOS ${nosVersion}                    `
        const webMessage = `Web: #${webCommitHash.slice(0, 7)} / ${formatDuration(webDuration)} ago`
        const apiMessage = `API: #${apiCommitHash.slice(0, 7)} / ${formatDuration(apiDuration)} ago`

        console.info(versionMessage, css)
        console.info(webMessage)
        console.info(apiMessage)

        hasBeenLogged = true
      }
    }

    const intervalId = setInterval(() => {
      const isOpen = window.outerHeight - window.innerHeight > 100
      if (isOpen) {
        logBuildInfo()
      }
    }, 1000)

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(intervalId)
    }
  }, [apiBuildDate, apiCommitHash, nosVersion, webBuildDate, webCommitHash])
}
